/* eslint-disable react-native/no-color-literals */
/* eslint-disable react-native/no-inline-styles */
/*
Author: Gilang Romadhanu Tartila
*/

import React, { useRef, useEffect } from 'react';
import {
  SafeAreaView,
  useSafeAreaInsets,
} from 'react-native-safe-area-context';
import { useSelector } from 'react-redux';
import {
  View,
  Animated,
  Modal,
  TouchableOpacity,
  StyleSheet,
  Dimensions,
  KeyboardAvoidingView,
  ActivityIndicator,
} from 'react-native';
import { Portal } from '@gorhom/portal';
import Colors from '@global/colors';
import { getEnvironment } from '@global/helpers';
import { isWeb, isMobileWeb, isNative, isAndroid } from '@global/screens';
import useAndroidKeyboardHeight from '@/hooks/useAndroidKeyboardHeight';
import PropTypes from 'prop-types';

const propTypes = {
  testID: PropTypes.string,
  closeOutside: PropTypes.bool,
  style: PropTypes.any,
  visible: PropTypes.bool,
  fullPage: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  transparent: PropTypes.bool,
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  maxWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]),
  animationType: PropTypes.string,
  needTop: PropTypes.any,
  spaceFromTop: PropTypes.any,
  spaceFromLeft: PropTypes.any,
  centered: PropTypes.bool,
  onClose: PropTypes.func,
  zIndex: PropTypes.number,
};

const dimension = Dimensions.get('window');

const ModalDefault = (props) => {
  const {
    testID = 'modal-default', // unique identifier for automation testing
    closeOutside = true, // allow on close by clicking outside of modal
    style = {}, // custom modal style
    visible = false, // show/hide modal
    fullPage = false, // fullpage modal
    transparent = false, // transparent modal overlay
    width = '100%', // width modal
    maxWidth = '100%', // max width modal
    animationType = 'fade', // animation type for modal show/hide
    needTop = false, // no need top space
    spaceFromTop = 0, // add some space on top of modal
    spaceFromLeft = 0, // add some space on left of modal
    centered = false, // center modal vertically on web
    children = null,
    onClose = () => {}, // on close callback
    zIndex, // override stacking order, e.g. to render above a Portal-based drawer
  } = props;
  const contentZIndex = zIndex || 10;
  const overlayZIndex = zIndex ? zIndex - 1 : 9;
  const insets = useSafeAreaInsets();
  const Config = getEnvironment();
  const isShowingLogger = (!!Config.ENABLE_NETWORK_LOGGER && isNative) || false;

  const isLoading = useSelector((state) => state.modal.isLoading);
  const modalHeight =
    fullPage && isMobileWeb
      ? '100dvh'
      : dimension.height - (insets.top - insets.bottom - (!isWeb ? 0 : 55));

  // See the hook: Android needs the keyboard offset applied as margin here.
  const keyboardHeight = useAndroidKeyboardHeight();

  const fadeIn = useRef(new Animated.Value(0)).current;
  const fadeContentIn = useRef(new Animated.Value(0)).current;
  const slideIn = useRef(new Animated.Value(1000)).current;
  const navigationRef = useRef();

  useEffect(() => {
    if (!isNative) {
      if (visible) {
        Animated.parallel([
          Animated.timing(fadeIn, {
            toValue: 1,
            duration: 250,
            useNativeDriver: false,
          }),
          Animated.timing(slideIn, {
            toValue: 0,
            duration: 500,
            useNativeDriver: false,
          }),
          Animated.timing(fadeContentIn, {
            toValue: 1,
            duration: 250,
            useNativeDriver: false,
          }),
        ]).start();
      }
    }
  }, [props]);

  const onClosePopup = () => {
    try {
      if (!isNative) {
        Animated.parallel([
          Animated.timing(fadeIn, {
            toValue: 0,
            duration: 250,
            useNativeDriver: false,
          }),
          Animated.timing(slideIn, {
            toValue: 1000,
            duration: 500,
            useNativeDriver: false,
          }),
          Animated.timing(fadeContentIn, {
            toValue: 0,
            duration: 250,
            useNativeDriver: false,
          }),
        ]).start();
      }
      setTimeout(() => {
        onClose();
      }, 500);
    } catch (error) {}
  };

  let NetworkLoggerFloating;
  if (isNative) {
    try {
      NetworkLoggerFloating =
        require('@/components/global/NetworkLoggerFloating').default;
    } catch (e) {}
  }

  return isNative ? (
    <Modal {...props} statusBarTranslucent navigationBarTranslucent>
      {isShowingLogger && <NetworkLoggerFloating navigation={navigationRef} />}
      <KeyboardAvoidingView
        testID={testID}
        behavior={isAndroid ? undefined : 'height'}
        style={{
          flex: 1,
          ...(isAndroid ? { marginBottom: keyboardHeight } : {}),
        }}>
        {isLoading ? (
          <View style={styles.overlay}>
            <View style={styles.loader}>
              <ActivityIndicator size="large" color={Colors.blue0} />
            </View>
          </View>
        ) : (
          false
        )}
        {fullPage ? (
          children
        ) : (
          <>
            <SafeAreaView
              style={{ flex: 0, backgroundColor: Colors.blue1 }}
              onPress={() => (closeOutside ? onClosePopup() : false)}
            />
            <SafeAreaView style={{ flex: 1 }}>
              <TouchableOpacity
                activeOpacity={1}
                style={styles.modalOverlay}
                onPress={() =>
                  closeOutside ? onClosePopup() : false
                }></TouchableOpacity>
              {children}
            </SafeAreaView>
          </>
        )}
      </KeyboardAvoidingView>
    </Modal>
  ) : visible ? (
    <Portal>
      <Animated.View
        testID={testID}
        style={{
          ...StyleSheet.absoluteFillObject,
          position: !isNative ? 'fixed' : 'absolute',
          top: 0,
          bottom: 0,
          right: 0,
          left: 0,
          opacity: fadeIn,
          zIndex: overlayZIndex,
          elevation: overlayZIndex,
          paddingTop: isMobileWeb ? 0 : 25,
          alignItems: centered
            ? 'center'
            : isMobileWeb
            ? 'flex-end'
            : 'flex-start',
          justifyContent: 'center',
          flexDirection: 'row',
        }}>
        <TouchableOpacity
          activeOpacity={1}
          style={styles.modalOverlay}
          onPress={() =>
            closeOutside ? onClosePopup() : false
          }></TouchableOpacity>
        <Animated.View
          style={{
            ...style,
            ...(animationType == 'slide'
              ? {
                  transform: [
                    {
                      translateY: slideIn,
                    },
                  ],
                }
              : {
                  opacity: fadeContentIn,
                }),
            backgroundColor: transparent ? 'transparent' : Colors.white,
            width: width || 600,
            maxWidth: maxWidth || '100%',
            maxHeight: modalHeight,
            borderRadius: 5,
            position: !isNative ? 'fixed' : 'relative',
            zIndex: contentZIndex,
            elevation: contentZIndex,
            ...(fullPage && isMobileWeb ? { top: 0 } : {}),
            ...(needTop && isMobileWeb ? { top: 0 } : {}),
            ...(spaceFromTop ? { top: spaceFromTop } : {}),
            ...(spaceFromLeft ? { left: spaceFromLeft } : {}),
          }}>
          {children}
        </Animated.View>
      </Animated.View>
    </Portal>
  ) : (
    false
  );
};

const colorBlack = '#00000099';
const styles = StyleSheet.create({
  modalOverlay: {
    position: isMobileWeb ? 'fixed' : 'absolute',
    top: 0,
    bottom: 0,
    right: 0,
    left: 0,
    backgroundColor: colorBlack,
  },
  overlay: {
    width: dimension.width,
    height: dimension.height,
    borderRadius: 5,
    position: !isNative ? 'fixed' : 'absolute',
    zIndex: 10,
    elevation: 10,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colorBlack,
  },
  loader: { width: '100%', paddingVertical: 15 },
});

ModalDefault.propTypes = propTypes;

export default React.memo(ModalDefault, (prev, next) => prev == next);
